import { WorkflowKind } from '@augloop/interfaces';
import { Log, LogCategory, Operation as LogOperation } from '@augloop/logging';
export class WorkflowGraph {
    constructor() {
        this.graphNodeByLocationAndWorkflowId = new Map();
    }
    getWorkflow(workflowId, external) {
        var _a;
        const location = this.getLocation(external);
        return (_a = this.graphNodeByLocationAndWorkflowId.get(location)) === null || _a === void 0 ? void 0 : _a.get(workflowId);
    }
    addWorkflow(workflow, external = false) {
        const logOp = new LogOperation({ operationName: 'WorkflowGraph', success: true, resourceId: workflow.id, resultDescription: `Adding workflow to the graph: ${external}` }).start();
        const location = this.getLocation(external);
        if (this.getWorkflow(workflow.id, external)) {
            logOp.success = false;
            logOp.resultDescription = `Adding a new with workflow with a duplicated workflowId: ${external}`;
            Log.error(0x1f403156 /* tag_5qdfw */, LogCategory.CoreDefault, logOp.stop());
            return;
        }
        const workflowGraphNode = new GraphNode(workflow, location);
        this.addWorkflowAsDependency(workflowGraphNode);
        let workflows = this.graphNodeByLocationAndWorkflowId.get(location);
        if (!workflows) {
            workflows = new Map();
            this.graphNodeByLocationAndWorkflowId.set(location, workflows);
        }
        workflows.set(workflow.id, workflowGraphNode);
        Log.info(0x1f0a06c8 /* tag_5c61i */, LogCategory.CoreDefault, logOp.stop());
    }
    getUpstreamRuntimeVisibleWorkflows() {
        /**
         * We consider the following factors when building conceptual workflow compressions:
         *
         *  - All of them in Default visibility (items cross instance boundaries).
         *  - The ones with no input types are not added to the final list.
         *  - The ones with no output type are valid and are on the final list (only LocalOnly chain).
         *  - LocalOnly get output types from downstream workflows.
         */
        const externalWorkflows = [];
        for (const graphNodesMap of this.graphNodeByLocationAndWorkflowId.values() || []) {
            for (const node of graphNodesMap.values() || []) {
                const virtualWorkflow = this.createWorkflowDefinition(node.workflow);
                if (node.workflow.visibility === 1 /* LocalOnly */) {
                    // Compress downstream workflows LocalOnly output types
                    this.compressDownstreamWorkflows(virtualWorkflow, node);
                }
                else {
                    virtualWorkflow.outputTypes.push(...node.workflow.outputTypes);
                }
                if (virtualWorkflow.inputTypes.length !== 0) {
                    externalWorkflows.push(virtualWorkflow);
                }
            }
        }
        return externalWorkflows;
    }
    removeWorkflows(external) {
        const location = this.getLocation(external);
        const graphNodesMap = this.graphNodeByLocationAndWorkflowId.get(location);
        const graphNodes = Array.from((graphNodesMap === null || graphNodesMap === void 0 ? void 0 : graphNodesMap.values()) || []);
        for (const graphNode of graphNodes) {
            for (const upstreamNode of graphNode.upstreamWorkflows.values()) {
                upstreamNode.downstreamWorkflows.delete(graphNode);
            }
            for (const downstreamNode of graphNode.downstreamWorkflows.values()) {
                downstreamNode.upstreamWorkflows.delete(graphNode);
            }
            graphNodesMap.delete(graphNode.workflow.id);
        }
    }
    getWorkflowsDefinitions() {
        const workflowDefinitions = [];
        for (const graphNodesMap of this.graphNodeByLocationAndWorkflowId.values() || []) {
            for (const node of graphNodesMap.values() || []) {
                workflowDefinitions.push(node.workflow);
            }
        }
        return workflowDefinitions;
    }
    getLocation(external) {
        return external ? 1 /* External */ : 0 /* Local */;
    }
    createWorkflowDefinition(workflow) {
        const virtualWorkflow = {
            id: `${workflow.id}-Virtual`,
            kind: workflow.kind,
            visibility: 0 /* Default */,
            inputTypes: workflow.inputTypes,
            outputTypes: [],
            collectionScopeType: workflow.collectionScopeType,
            correlatedSignals: workflow.correlatedSignals
        };
        return virtualWorkflow;
    }
    compressDownstreamWorkflows(vw, workflowNode) {
        for (const downstreamWorkflow of workflowNode.downstreamWorkflows) {
            if (downstreamWorkflow.workflow.visibility === 1 /* LocalOnly */) {
                this.compressDownstreamWorkflows(vw, downstreamWorkflow);
                continue;
            }
            if (downstreamWorkflow.workflow.kind === WorkflowKind.Join && vw.inputTypes.indexOf(downstreamWorkflow.workflow.collectionScopeType) === -1) {
                continue;
            }
            for (const outputType of downstreamWorkflow.workflow.outputTypes) {
                if (vw.outputTypes.indexOf(outputType) === -1) {
                    vw.outputTypes.push(outputType);
                }
            }
        }
    }
    addWorkflowAsDependency(graphNode) {
        for (const graphNodeByWorkflowId of this.graphNodeByLocationAndWorkflowId.values()) {
            for (const workflowNode of graphNodeByWorkflowId.values()) {
                for (const inputType of workflowNode.workflow.inputTypes) {
                    if (graphNode.workflow.outputTypes.indexOf(inputType) !== -1) {
                        this.addWorkflowChain(graphNode, workflowNode);
                    }
                }
                for (const outputType of workflowNode.workflow.outputTypes) {
                    if (graphNode.workflow.inputTypes.indexOf(outputType) !== -1) {
                        this.addWorkflowChain(workflowNode, graphNode);
                    }
                }
            }
        }
    }
    addWorkflowChain(upstreamWorkflow, downstreamWorkflow) {
        upstreamWorkflow.downstreamWorkflows.add(downstreamWorkflow);
        downstreamWorkflow.upstreamWorkflows.add(upstreamWorkflow);
    }
}
class GraphNode {
    constructor(workflow, location) {
        this.upstreamWorkflows = new Set();
        this.downstreamWorkflows = new Set();
        this.location = location;
        this.workflow = Object.assign(Object.assign({}, workflow), { inputTypes: !Array.isArray(workflow.inputTypes) ? [] : workflow.inputTypes, outputTypes: !Array.isArray(workflow.outputTypes) ? [] : workflow.outputTypes });
    }
}
//# sourceMappingURL=workflow-graph.js.map